import { Analyse } from "../state/schema";

/* API-Client. Im Demo-Modus (VITE_APP_MODE=demo) werden Vorgangsnummer,
   Uploads und Absenden lokal simuliert – ohne Google/Gmail. Im Live-Modus
   würden hier die Netlify Functions unter /api/* aufgerufen. */

const MODE = (import.meta.env.VITE_APP_MODE as string) || "demo";
export const IS_DEMO = MODE !== "live";

/** Erzeugt eine Vorgangsnummer im Format MWA-2026-XXXXXX.
 *  Im Live-Betrieb kommt diese serverseitig (create-process). */
export function makeVorgangsnummer(): string {
  const year = new Date().getFullYear();
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // ohne verwechselbare Zeichen
  let part = "";
  const buf = new Uint32Array(6);
  crypto.getRandomValues(buf);
  for (let i = 0; i < 6; i++) part += alphabet[buf[i] % alphabet.length];
  return `MWA-${year}-${part}`;
}

export interface UploadHandle {
  driveFileId: string;
}

/** Simuliert einen Upload mit Fortschritt. Live: resumable Upload zu Drive. */
export function demoUpload(
  _file: File,
  onProgress: (pct: number) => void
): { promise: Promise<UploadHandle>; abort: () => void } {
  let aborted = false;
  let timer: number | undefined;
  const promise = new Promise<UploadHandle>((resolve, reject) => {
    let pct = 0;
    const tick = () => {
      if (aborted) {
        reject(new Error("abgebrochen"));
        return;
      }
      pct = Math.min(100, pct + 12 + Math.random() * 18);
      onProgress(Math.round(pct));
      if (pct >= 100) {
        resolve({ driveFileId: "demo-" + Math.random().toString(36).slice(2, 11) });
      } else {
        timer = window.setTimeout(tick, 140);
      }
    };
    timer = window.setTimeout(tick, 200);
  });
  return {
    promise,
    abort: () => {
      aborted = true;
      if (timer) window.clearTimeout(timer);
    },
  };
}

/** Holt eine serverseitige Vorgangsnummer (Live) bzw. erzeugt sie lokal (Demo). */
export async function createProcess(kundenname = ""): Promise<string> {
  if (IS_DEMO) return makeVorgangsnummer();
  const res = await fetch("/api/create-process", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ kundenname }),
  });
  if (!res.ok) throw new Error("No se pudo crear el expediente.");
  const data = await res.json();
  return typeof data?.vorgangsnummer === "string" ? data.vorgangsnummer : makeVorgangsnummer();
}

export interface UploadContext {
  kategorie: string;
  vorgangsnummer: string;
  kundenname: string;
}

/** Wählt automatisch Demo- oder Live-Upload (gleiche Schnittstelle wie demoUpload). */
export function startUpload(
  file: File,
  ctx: UploadContext,
  onProgress: (pct: number) => void
): { promise: Promise<UploadHandle>; abort: () => void } {
  if (IS_DEMO) return demoUpload(file, onProgress);
  return liveUpload(file, ctx, onProgress);
}

/** Live-Upload: upload-init → direkt zur resumable Drive-Session → upload-finalize. */
function liveUpload(
  file: File,
  ctx: UploadContext,
  onProgress: (pct: number) => void
): { promise: Promise<UploadHandle>; abort: () => void } {
  let aborted = false;
  let xhr: XMLHttpRequest | undefined;

  const promise = (async (): Promise<UploadHandle> => {
    // 1) Upload autorisieren + resumable Session beschaffen.
    const initRes = await fetch("/api/upload-init", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        kategorie: ctx.kategorie,
        filename: file.name,
        mime: file.type || "application/octet-stream",
        size: file.size,
        vorgangsnummer: ctx.vorgangsnummer,
        kundenname: ctx.kundenname,
      }),
    });
    if (!initRes.ok) throw new Error("No se pudo iniciar la subida.");
    const init = await initRes.json();
    const uploadUrl: string | null = init?.uploadUrl ?? null;
    if (!uploadUrl) throw new Error("No se recibió la URL de subida.");
    if (aborted) throw new Error("cancelado");

    // 2) Datei direkt zur Drive-Session hochladen (mit Fortschritt).
    const driveFileId = await new Promise<string>((resolve, reject) => {
      xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl, true);
      xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
      };
      xhr.onload = () => {
        if (xhr!.status >= 200 && xhr!.status < 300) {
          try {
            const r = JSON.parse(xhr!.responseText);
            if (r?.id) resolve(String(r.id));
            else reject(new Error("Respuesta de subida sin ID de archivo."));
          } catch {
            reject(new Error("Respuesta de subida ilegible."));
          }
        } else {
          reject(new Error("La subida falló (" + xhr!.status + ")."));
        }
      };
      xhr.onerror = () => reject(new Error("Error de red durante la subida."));
      xhr.onabort = () => reject(new Error("cancelado"));
      xhr.send(file);
    });

    // 3) Upload serverseitig bestätigen.
    const finRes = await fetch("/api/upload-finalize", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ vorgangsnummer: ctx.vorgangsnummer, kategorie: ctx.kategorie, driveFileId }),
    });
    if (!finRes.ok) throw new Error("Falló la confirmación de la subida.");
    onProgress(100);
    return { driveFileId };
  })();

  return {
    promise,
    abort: () => {
      aborted = true;
      xhr?.abort();
    },
  };
}

export interface SubmitResult {
  vorgangsnummer: string;
  beraterLabel: string;
  /** En producción el servidor devuelve el PDF ya generado (Base64) para la descarga. */
  pdfBase64?: string;
}

/** Simuliert das Absenden. Live: POST /api/submit (Validierung, PDF, Drive, Mail). */
export async function submitAnalyse(a: Analyse): Promise<SubmitResult> {
  if (IS_DEMO) {
    await new Promise((r) => setTimeout(r, 900));
    return {
      vorgangsnummer: a.meta.vorgangsnummer,
      beraterLabel: "", // wird in der UI aus der Kennung abgeleitet
    };
  }
  const res = await fetch("/api/submit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(a),
  });
  if (!res.ok) throw new Error("El envío falló.");
  return (await res.json()) as SubmitResult;
}

/** Convierte una cadena Base64 en un Uint8Array (para la descarga del PDF). */
export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}
